import { action, observable, runInAction } from 'mobx';
import React from 'react';

import identityClient, {
  JoinableSite,
  JoinableSiteProductData,
  JoinableSiteProducts,
  JoinableSitesResponse,
  ProductContext,
} from '../clients/identityClient';
import worklensClient, { AvailableSite } from '../clients/WorklensClient';
import {
  getProductType,
  getProductTypeFromWorklensType,
  PRODUCT_TYPE,
  SUPPORTED_PRODUCT_TYPES,
} from '../model/productType';
import { JoinableProduct } from '../ui/Products/JoinableProductCard';

export interface Product {
  cloudId: string;
  count: number;
  icon: typeof React.Component;
  productName: string;
  siteName: string;
  siteRelevance?: number;
  siteSize?: number;
  url: string;
}

export interface Site<P extends Product = Product> {
  adminAccess: boolean;
  cloudId: string;
  name: string;
  products: P[];
  relevance?: number;
  size?: number;
  url: string;
}

export default class ProductStore {
  @observable availableProducts: Product[] = [];
  @observable availableSites: Site<Product>[] = [];
  @observable hasLoaded: boolean = false;
  @observable isUserAdmin: boolean = false;
  @observable joinableProducts: JoinableProduct[] = [];
  @observable joinableSites: Site<JoinableProduct>[] = [];

  static async fetchJoinableSites(
    shouldFetchCollaborators?: boolean,
    productContext?: ProductContext,
  ): Promise<JoinableSitesResponse> {
    try {
      if (shouldFetchCollaborators) {
        const joinableSitesWithCollaborators = await identityClient.getJoinableSitesWithCollaborators(productContext);
        return Promise.resolve({ joinableSites: joinableSitesWithCollaborators.sites });
      } else {
        return await identityClient.getJoinableSites();
      }
    } catch (e) {
      return { joinableSites: [] };
    }
  }

  @action
  async getProducts() {
    try {
      const availableSites = await worklensClient.getAvailableProducts();

      runInAction(() => {
        this.availableSites = mapAvailableSites(availableSites.sites);
        this.availableProducts = mapProducts(this.availableSites);

        this.joinableSites = [];
        this.joinableProducts = [];

        this.isUserAdmin = this.availableSites.some(site => site.adminAccess);
        this.hasLoaded = true;
      });
    } catch (e) {
      // Don't want this error to propagate up
      // tslint:disable-next-line:no-console
      console.error(e);
    }
  }

  @action
  async getProductsAndJoinableSites(shouldFetchCollaborators?: boolean) {
    try {
      const availableSitesResponse = await worklensClient.getAvailableProducts();
      const availableSites = mapAvailableSites(availableSitesResponse.sites);
      const availableProducts = mapProducts(availableSites);

      // This joinable sites call will soon require data from availableSites
      const collaboratorsContext = getProductContext(availableProducts);
      const joinableSitesResponse = await ProductStore.fetchJoinableSites(
        shouldFetchCollaborators,
        collaboratorsContext,
      );
      const joinableSites = mapJoinableSites(joinableSitesResponse.joinableSites);
      const joinableProducts = mapProducts(joinableSites);

      runInAction(() => {
        this.availableSites = availableSites;
        this.availableProducts = availableProducts;

        this.joinableSites = joinableSites;
        this.joinableProducts = joinableProducts;

        this.isUserAdmin = this.availableSites.filter(site => site.adminAccess).length > 0;
        this.hasLoaded = true;
      });
    } catch (e) {
      // Don't want this error to propagate up
      // tslint:disable-next-line:no-console
      console.error(e);
    }
  }
}

const getProductContext = (products: Product[]): ProductContext | undefined => {
  // Finds highest 'priority' jira or confluence product the user has access to,
  // products are already sorted by activity.
  const productForContext = products.find(e => e.productName === 'Jira Software' || e.productName === 'Confluence');
  if (productForContext) {
    const productKey = productForContext.productName === 'Jira Software' ? 'jira' : 'confluence';
    return { siteId: productForContext.cloudId, productKey };
  }
};

const mapProducts = <P extends Product>(sites: Site<P>[]): P[] => {
  return sites
    .map((site: Site<P>) => site.products)
    .reduce((acc, value) => acc.concat(value), [])
    .sort((a, b) => b.count - a.count);
};

const getJoinableProductKeys = (products: JoinableSiteProducts | PRODUCT_TYPE[]): PRODUCT_TYPE[] =>
  Array.isArray(products) ? products : (Object.keys(products) as PRODUCT_TYPE[]);

const getJoinableProduct = (
  products: JoinableSiteProducts | PRODUCT_TYPE[],
  productKey: PRODUCT_TYPE,
): JoinableSiteProductData => (Array.isArray(products) ? { collaborators: [] } : products[productKey]!);

export const mapJoinableSites = (joinableSites: JoinableSite[]): Site<JoinableProduct>[] => {
  return joinableSites
    .map((joinableSite: JoinableSite) => {
      const { cloudId, displayName: name, url, size } = joinableSite;
      const joinableProductKeys: PRODUCT_TYPE[] = getJoinableProductKeys(joinableSite.products);
      // New endpoint returns object with key: 'product key', value: [Collaborator]
      // The old one returns an array of product keys
      const joinableProducts: JoinableProduct[] = joinableProductKeys
        .filter(productKey => SUPPORTED_PRODUCT_TYPES.includes(productKey))
        .map(productKey => {
          const type = getProductType(productKey);
          const joinableProduct: JoinableSiteProductData = getJoinableProduct(joinableSite.products, productKey);
          return {
            cloudId: joinableSite.cloudId,
            collaborators: joinableProduct.collaborators || [],
            count: 0,
            icon: type.icon,
            productKey,
            productName: type.name,
            siteName: joinableSite.displayName,
            siteRelevance: joinableSite.relevance,
            siteSize: size,

            // XXX: As of now, Jira routing has a bug where deep-linking into Jira, if you don't have permissions,
            // will redirect to a project error page (a dead end) instead of the DES join page (through which we want
            // users to join).
            // The endpoint we're using is aware of that and will ensure that for Jira, it'll return `productUrl`s
            // that just link to site root: `<site>/`. These will correctly show the DES join page.
            // If the `productUrl` is missing for some reason, we can only fall back to the site `url`
            // to get that safe site root url.
            url: joinableProduct.productUrl || joinableSite.url,
          };
        });
      return {
        adminAccess: false,
        cloudId,
        name,
        products: joinableProducts,
        url,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

const mapAvailableSites = (sites: AvailableSite[]): Site<Product>[] => {
  return sites
    .map((site: AvailableSite) => {
      const { adminAccess, cloudId, displayName: name, url } = site;
      // only map supported products
      const products = site.availableProducts.map(({ activityCount, productType, url: productUrl }) => {
        const type = getProductTypeFromWorklensType(productType);
        return {
          cloudId,
          count: activityCount,
          icon: type.icon,
          productName: type.name,
          siteName: name,
          url: productUrl || url + type.url,
        };
      });
      return {
        adminAccess: Boolean(adminAccess),
        cloudId,
        name,
        products,
        url,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};
